/**
 * L8 — Quote Layout
 * Person photo on left, large quote text on right.
 * Perfect for statements, reactions, interviews.
 */
import React from 'react';
import { AbsoluteFill } from 'remotion';
import type { SceneProps } from '../../types';
import {
  SceneContainer, Visual, SubheadlineText,
  GlobalOverlays, useTextEnter,
} from './shared';

export const L8_QuoteLayout: React.FC<SceneProps> = (props) => {
  const { quoteText, headline, subheadline, assets, colorPalette, headlineFont, subheadlineFont } = props;
  const person = assets.personPhoto ?? assets.primary;

  const quoteEnter = useTextEnter(4, 16);

  return (
    <SceneContainer bg={colorPalette.primary}>
      {/* Decorative background */}
      <AbsoluteFill>
        <div style={{
          position: 'absolute', inset: 0,
          background: `linear-gradient(135deg, ${colorPalette.primary} 0%, ${colorPalette.secondary} 100%)`,
        }} />
        {/* Large quote mark */}
        <div style={{
          position: 'absolute', top: 60, right: 80,
          fontSize: 300, fontFamily: 'Georgia, serif',
          color: colorPalette.accent, opacity: 0.07,
          lineHeight: 1, userSelect: 'none',
        }}>"</div>
      </AbsoluteFill>

      <AbsoluteFill style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
        {/* Left: person photo */}
        <div style={{
          width: '38%', height: '100%',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: '80px 0 100px 80px',
          boxSizing: 'border-box',
        }}>
          <div style={{
            width: '100%', aspectRatio: '3/4',
            borderRadius: 16, overflow: 'hidden',
            border: `3px solid ${colorPalette.accent}`,
            boxShadow: `0 0 40px ${colorPalette.accent}40`,
          }}>
            <Visual asset={person} objectFit="cover" layerKey="primaryMedia" />
          </div>
        </div>

        {/* Right: quote + attribution */}
        <div style={{
          flex: 1, padding: '80px 80px 100px 48px',
          boxSizing: 'border-box',
          display: 'flex', flexDirection: 'column', justifyContent: 'center',
        }}>
          {/* Opening quote mark */}
          <div style={{
            fontSize: 80, fontFamily: 'Georgia, serif',
            color: colorPalette.accent, lineHeight: 0.6,
            marginBottom: 16, ...quoteEnter,
          }}>"</div>

          {/* Quote text */}
          <div style={{
            fontFamily: headlineFont.fontFamily,
            fontSize: Math.min(headlineFont.fontSize * 0.9, 42),
            fontWeight: 600, color: '#fff',
            lineHeight: 1.4,
            display: '-webkit-box', WebkitLineClamp: 5,
            WebkitBoxOrient: 'vertical', overflow: 'hidden',
            ...quoteEnter,
          }}>
            {quoteText ?? headline}
          </div>

          {/* Attribution */}
          {(subheadline || headline) && (
            <SubheadlineText
              text={subheadline ?? headline}
              font={{
                ...subheadlineFont,
                color: colorPalette.accent,
                fontSize: 22, fontWeight: 700,
              }}
              delay={20}
              style={{ marginTop: 20 }}
              layerKey="subheadline"
            />
          )}
        </div>
      </AbsoluteFill>

      <GlobalOverlays props={props} />
    </SceneContainer>
  );
};
